import type * as React from "react";
import { useCallback, useMemo } from "react";
import FilterGroupItem from "../../FilterGroupItem";
import SingleTechFilter from "./SingleTechFilter";

import { useDispatch, useSelector } from "react-redux";
import { applyTechValueGroup, clearTechValuesGroup, updateTechValueAction } from "../../../../redux/actions";
import { getIsClearEnabled } from "../../../../redux/searchSelectors";
import { type RootState } from "../../../../../redux/store";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";
import { Box } from "@mui/material";
import { verticalStacker } from "~/styles/styledMixins";
import type { TechValueFilterCategoryData } from "~/Search/filterConfigModels";

interface Props {
  data: TechValueFilterCategoryData;
  hidden?: boolean;
  hideTechFilterButtons?: boolean;
}

type CallbacksMap = {
  [title: string]: (type: "min" | "max", value: number | undefined) => void;
};

export const InnerTechFilterCategory = (props: Props) => {
  const dispatch = useDispatch();

  const values = useSelector((state: RootState) => state.srp.rawTechFilterValues);

  const { items } = props.data;
  const { t } = useTranslation();
  const callbacks = useMemo(() => {
    const callbacks: CallbacksMap = {};
    for (const item of items) {
      callbacks[item.uid] = (type, val) => dispatch(updateTechValueAction(item.uid, type, val));
    }
    return callbacks;
  }, [items, dispatch]);

  const remodeledItems = items.map((obj) => {
    const titleTranslated = <span dangerouslySetInnerHTML={{ __html: t(obj.title) + (obj.unit && ` ${obj.unit}`) }} />;
    return { ...obj, title: titleTranslated };
  });

  return remodeledItems.map((item) => (
    <SingleTechFilter
      key={item.uid}
      title={item.title}
      min={values[`${item.uid}.min`]}
      max={values[`${item.uid}.max`]}
      onUpdateValues={callbacks[item.uid]}
      data-test-id={item.uid}
    />
  ));
};

export const TechFilterCategory = (props: Props) => {
  const dispatch = useDispatch();

  const onClearClick = () => dispatch(clearTechValuesGroup(props.data.items.map((item) => item.title)));

  const values = useSelector((state: RootState) => state.srp.rawTechFilterValues);
  const isClearEnabled = useSelector((state: RootState) =>
    getIsClearEnabled(
      state,
      props.data.items.map((item) => item.title),
    ),
  );

  const { title, initiallyExpanded } = props.data;
  const { t } = useTranslation();

  const applyCb = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      dispatch(applyTechValueGroup());
    },
    [dispatch],
  );

  const toExpand =
    initiallyExpanded ||
    props.data.items.find((filter) => values[filter.uid + ".min"] || values[filter.uid + ".max"]) != undefined;
  return (
    <FilterGroupItem
      title={title}
      initiallyExpanded={toExpand}
      data-test-id={props.data.facetId ?? undefined}
      hidden={props.hidden}
    >
      <form onSubmit={applyCb}>
        <Box sx={{ ...verticalStacker(), gap: "32px" }}>
          <InnerTechFilterCategory data={props.data} hidden={props.hidden} />
          {!props.hideTechFilterButtons && (
            <Box sx={{ display: "flex", gap: "16px", justifyContent: "flex-end" }}>
              <Button
                variant={"text"}
                type={"button"}
                disabled={!isClearEnabled}
                onClick={onClearClick}
                data-test-id={`tech-filter-category-${props.data.facetId}-reset`}
              >
                {t("searchPage.refinement.button.clear")}
              </Button>
              <Button
                type={"submit"}
                disabled={!isClearEnabled}
                data-test-id={`tech-filter-category-${props.data.facetId}-apply`}
              >
                {t("searchPage.refinement.button.apply")}
              </Button>
            </Box>
          )}
        </Box>
      </form>
    </FilterGroupItem>
  );
};
